import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import {
  getChannelsWithCache,
  getChannelOptions,
  getChannelName,
  getChannelDescription,
  getChannelColor,
  getDefaultChannels,
  type TVChannelInfo
} from '@/api/TVChannels'
import type { TVChannel } from '@/types'
import { debugError, debugWarn } from '@/utils/env'

export const useChannelStore = defineStore('channel', () => {
  // 状态
  const channels = ref<TVChannelInfo[]>([])
  const channelOptions = ref<{ value: TVChannel; label: string }[]>([])
  const loading = ref(false)
  const error = ref<string | null>(null)

  // 频道图标映射
  const channelIcons: Record<string, string> = {
    'bbc': 'fas fa-tv',
    'nhk': 'fas fa-torii-gate',
    'cctv': 'fas fa-broadcast-tower',
    'discovery': 'fas fa-compass',
    'NG': 'fas fa-camera',
    'hbo': 'fas fa-video',
    'netflix': 'fas fa-play',
    'amazon': 'fab fa-amazon',
    'apple': 'fab fa-apple',
    'youtube': 'fab fa-youtube'
  }

  // 动态 ID -> Code 映射
  const channelIdToCode = computed(() => {
    const map: Record<number, TVChannel> = {}
    channels.value.forEach(ch => {
      map[ch.id] = ch.code as TVChannel
    })
    return map
  })

  // 动态 Code -> ID 映射
  const channelCodeToId = computed(() => {
    const map: Record<string, number> = {}
    channels.value.forEach(ch => {
      map[ch.code] = ch.id
    })
    return map
  })

  // 获取所有频道
  const fetchChannels = async () => {
    if (loading.value || channels.value.length > 0) return channels.value

    loading.value = true
    error.value = null

    try {
      const channelData = await getChannelsWithCache()
      channels.value = channelData
      
      // 同时更新频道选项
      try {
        const options = await getChannelOptions()
        channelOptions.value = [
          { value: 'all' as TVChannel, label: '全部' },
          ...options
        ]
      } catch (optionsError) {
        debugWarn('获取频道选项失败，使用默认选项:', optionsError)
      }

      return channelData
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : '获取频道数据失败'
      error.value = errorMessage
      debugError('获取频道数据失败:', err)
      
      // 即使API失败，也使用默认频道数据
      const defaultChannels = getDefaultChannels()
      channels.value = defaultChannels
      
      return defaultChannels
    } finally {
      loading.value = false
    }
  }

  // 获取频道选项
  const fetchChannelOptions = async () => {
    if (channelOptions.value.length > 1) return channelOptions.value

    await fetchChannels()
    return channelOptions.value
  }

  // 获取频道名称
  const getChannelDisplayName = async (code: string): Promise<string> => {
    if (code === 'all') return '全部'

    // 先从本地缓存查找
    const channel = channels.value.find(c => c.code === code)
    if (channel) return channel.name

    // 如果本地没有，调用API
    try {
      return await getChannelName(code)
    } catch (error) {
      debugError('获取频道名称失败:', error)
      return code.toUpperCase()
    }
  }

  // 获取频道描述
  const getChannelDisplayDescription = async (code: string): Promise<string> => {
    if (code === 'all') return '所有频道'

    // 先从本地缓存查找
    const channel = channels.value.find(c => c.code === code)
    if (channel) return channel.description

    // 如果本地没有，调用API
    try {
      return await getChannelDescription(code)
    } catch (error) {
      debugError('获取频道描述失败:', error)
      return ''
    }
  }

  // 获取频道颜色
  const getChannelDisplayColor = async (code: string): Promise<string> => {
    if (code === 'all') return '#666666'

    // 先从本地缓存查找
    const channel = channels.value.find(c => c.code === code)
    if (channel) return channel.color

    // 如果本地没有，调用API
    try {
      return await getChannelColor(code)
    } catch (error) {
      debugError('获取频道颜色失败:', error)
      return '#666666'
    }
  }

  // 获取频道图标
  const getChannelIcon = (code: string): string => {
    return channelIcons[code] || 'fas fa-tv'
  }

  // 根据code获取频道信息
  const getChannelByCode = (code: string): TVChannelInfo | undefined => {
    return channels.value.find(c => c.code === code)
  }

  // 清除错误
  const clearError = () => {
    error.value = null
  }

  // 重置store
  const reset = () => {
    channels.value = []
    channelOptions.value = []
    loading.value = false
    error.value = null
  }

  return {
    // 状态
    channels,
    channelOptions,
    loading,
    error,
    channelIdToCode,
    channelCodeToId,
    
    // 方法
    fetchChannels,
    fetchChannelOptions,
    getChannelDisplayName,
    getChannelDisplayDescription,
    getChannelDisplayColor,
    getChannelIcon,
    getChannelByCode,
    clearError,
    reset
  }
})